import { useEffect } from 'react';
import styled from 'styled-components';

import {
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';
import type { TerminalTab } from '#/features/terminal/drawer-context';

import BlockTradeContextPump from '#/components/Terminal/BlockTradeContextPump';
import ChatView from '#/components/Terminal/ChatView';
import CronView from '#/components/Terminal/CronView';
import ExamplesView from '#/components/Terminal/ExamplesView';
import { SIDEBAR_WIDTH } from '#/components/Terminal/layout-constants';
import PendingMessageDrain from '#/components/Terminal/PendingMessageDrain';
import RecentsView from '#/components/Terminal/RecentsView';

export default function BlockTerminalTab() {
  return (
    <Container>
      <BlockTradeContextPump />
      <PendingMessageDrain />
      <Header />
      <Body />
    </Container>
  );
}

function Header() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();
  return (
    <HeaderRow>
      <Title>Block Terminal</Title>
      <Spacer />
      <HeaderControls>
        <SidebarToggleButton
          type="button"
          aria-label={
            drawerState.sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'
          }
          title={
            drawerState.sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'
          }
          onClick={drawerActions.toggleSidebar}
          $active={!drawerState.sidebarCollapsed}
        >
          <SidebarToggleIcon />
        </SidebarToggleButton>
        <PinSegment>
          <PinSegmentButton
            $active={drawerState.pinMode === 'bottom-tab'}
            onClick={() => drawerActions.setPinMode('bottom-tab')}
            type="button"
            title="Pin to bottom tab"
          >
            Bottom
          </PinSegmentButton>
          <PinSegmentButton
            $active={drawerState.pinMode === 'side-panel'}
            onClick={() => {
              drawerActions.setPinMode('side-panel');
              // Ensure drawer stays open when switching to side-panel
              if (!drawerState.isOpen) {
                drawerActions.open();
              }
            }}
            type="button"
            title="Pin as side panel"
          >
            Side
          </PinSegmentButton>
        </PinSegment>
      </HeaderControls>
    </HeaderRow>
  );
}

function Body() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();

  // The "Recents" tab used to be one of the top-level tabs; the
  // sidebar replaces it. If a previous session persisted
  // `activeTab: 'recents'` in localStorage, redirect to chat so the
  // main pane always has something concrete to render.
  useEffect(() => {
    if (drawerState.activeTab === ('recents' as TerminalTab)) {
      drawerActions.setActiveTab('chat');
    }
  }, [drawerState.activeTab, drawerActions]);

  return (
    <BodyContainer>
      {!drawerState.sidebarCollapsed && (
        <Sidebar>
          <RecentsView />
        </Sidebar>
      )}
      <MainPane>
        <TabPane $active={drawerState.activeTab === 'chat'}>
          <ChatView hideNewChatBar />
        </TabPane>
        <TabPane $active={drawerState.activeTab === 'examples'}>
          <ExamplesView />
        </TabPane>
        <TabPane $active={drawerState.activeTab === 'cron'}>
          <CronView />
        </TabPane>
      </MainPane>
    </BodyContainer>
  );
}

function SidebarToggleIcon() {
  // Bordered rectangle with a vertical divider — same glyph as the
  // Paradex `SidebarToggleIcon` so the action reads the same in both
  // apps.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <line
        x1="9"
        y1="4"
        x2="9"
        y2="20"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

const Container = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

const Title = styled.h2`
  margin: 0;
  padding: 0 4px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1;
  color: #fff;
  letter-spacing: 0.02em;
`;

const Spacer = styled.div`
  flex: 1;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SidebarToggleButton = styled.button<{ $active: boolean }>`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  color: ${(p) => (p.$active ? '#fff' : 'var(--text-secondary)')};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
`;

const PinSegment = styled.div`
  display: flex;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  overflow: hidden;
`;

const PinSegmentButton = styled.button<{ $active: boolean }>`
  padding: 3px 10px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${(p) =>
    p.$active ? 'var(--hover-bg, rgba(255, 255, 255, 0.08))' : 'transparent'};
  color: ${(p) =>
    p.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  border: none;
  cursor: pointer;
  transition: background 0.15s ease;

  & + & {
    border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  }

  &:hover {
    color: var(--text-primary);
  }
`;

const BodyContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  position: relative;
`;

const Sidebar = styled.div`
  flex-shrink: 0;
  width: ${SIDEBAR_WIDTH}px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

const MainPane = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TabPane = styled.div<{ $active: boolean }>`
  display: ${(p) => (p.$active ? 'flex' : 'none')};
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

// Re-export drawer types so other components can flag-gate without an
// extra import.
export type { PinMode, TerminalTab } from '#/features/terminal/drawer-context';
